import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Lock, AlertTriangle, Activity, Ban, Smartphone, Clock, MapPin, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecurityDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({});

  // 2FA
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [setup2FADialog, setSetup2FADialog] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);

  // Login History
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginPage, setLoginPage] = useState(1);

  // IP Blocking
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [blockIPDialog, setBlockIPDialog] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({ ip_address: '', reason: '' });

  // Anomalies
  const [anomalies, setAnomalies] = useState([]);

  // Security Events
  const [securityEvents, setSecurityEvents] = useState([]);

  useEffect(() => {
    fetchStats();
    checkUser2FA();
  }, []);

  useEffect(() => {
    if (activeTab === 'sessions') fetchSessions();
    else if (activeTab === 'login-history') fetchLoginHistory();
    else if (activeTab === 'ip-blocking') fetchBlockedIPs();
    else if (activeTab === 'anomalies') fetchAnomalies();
    else if (activeTab === 'events') fetchSecurityEvents();
  }, [activeTab, loginPage]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const checkUser2FA = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setTwoFactorEnabled(data.user?.totp_enabled || false);
    } catch (error) {
      console.error('Failed to check 2FA status');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/stats`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      toast.error('Failed to fetch security stats');
    }
  };

  // ============= 2FA =============

  const handleEnable2FA = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/security/2fa/enable`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        const data = await res.json();
        setQrCode(data.qr_code);
        setSecret(data.secret);
        toast.success('Scan QR code with your authenticator app');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to enable 2FA');
      }
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/security/2fa/verify`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code: verificationCode })
      });

      if (res.ok) {
        const data = await res.json();
        setRecoveryCodes(data.recovery_codes);
        setTwoFactorEnabled(true);
        toast.success('2FA enabled successfully!');
      } else {
        toast.error('Invalid verification code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;

    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/security/2fa/disable`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        setTwoFactorEnabled(false);
        setSetup2FADialog(false);
        setQrCode('');
        setSecret('');
        setPassword('');
        toast.success('2FA disabled');
      } else {
        toast.error('Failed to disable 2FA');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  // ============= SESSIONS =============

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/sessions`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (error) {
      toast.error('Failed to fetch sessions');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Revoke this session?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Session revoked');
        fetchSessions();
      } else {
        toast.error('Failed to revoke session');
      }
    } catch (error) {
      toast.error('Failed to revoke session');
    }
  };

  // ============= LOGIN HISTORY =============

  const fetchLoginHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/login-history?page=${loginPage}&limit=20`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setLoginHistory(data.logins || []);
    } catch (error) {
      toast.error('Failed to fetch login history');
    }
  };

  // ============= IP BLOCKING =============

  const fetchBlockedIPs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/blocked-ips`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setBlockedIPs(data.blocked_ips || []);
    } catch (error) {
      toast.error('Failed to fetch blocked IPs');
    }
  };

  const handleBlockIP = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/admin/security/blocked-ips`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(blockIPForm)
      });

      if (res.ok) {
        toast.success('IP blocked');
        setBlockIPDialog(false);
        setBlockIPForm({ ip_address: '', reason: '' });
        fetchBlockedIPs();
      } else {
        toast.error('Failed to block IP');
      }
    } catch (error) {
      toast.error('Failed to block IP');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockIP = async (blockId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/blocked-ips/${blockId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('IP unblocked');
        fetchBlockedIPs();
      } else {
        toast.error('Failed to unblock');
      }
    } catch (error) {
      toast.error('Failed to unblock');
    }
  };

  // ============= ANOMALIES =============

  const fetchAnomalies = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/anomalies`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setAnomalies(data.anomalies || []);
    } catch (error) {
      toast.error('Failed to fetch anomalies');
    }
  };

  // ============= SECURITY EVENTS =============

  const fetchSecurityEvents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/events?page=1&limit=20`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSecurityEvents(data.events || []);
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="security-dashboard">
      <div>
        <h1 className="text-3xl font-bold" data-font="display">Security Dashboard</h1>
        <p className="text-muted-foreground">Manage authentication, sessions, and security monitoring</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Logins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_logins || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed Logins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.failed_logins || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_sessions || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">2FA Enabled Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.users_with_2fa || 0}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="2fa">2FA</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="login-history">Login History</TabsTrigger>
          <TabsTrigger value="ip-blocking">IP Blocking</TabsTrigger>
          <TabsTrigger value="anomalies">Anomalies</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[hsl(351,96%,31%)]" />
                  Two-Factor Authentication
                </CardTitle>
              </CardHeader>
              <CardContent>
                {twoFactorEnabled ? (
                  <div className="space-y-2">
                    <Badge className="bg-green-50 text-green-700 border-green-200">Enabled</Badge>
                    <p className="text-sm text-muted-foreground">Your account is protected with 2FA</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Disabled</Badge>
                    <p className="text-sm text-muted-foreground">Enable 2FA for enhanced security</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Recent Security Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{stats.recent_security_events || 0}</p>
                <p className="text-sm text-muted-foreground">Events in last 24 hours</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* 2FA TAB */}
        <TabsContent value="2fa" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
              <CardDescription>Add an extra layer of security to your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!twoFactorEnabled && !qrCode && (
                <div className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      2FA uses your smartphone to generate time-based codes. You'll need an authenticator app like Google Authenticator or Authy.
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label htmlFor="password-2fa">Enter your password to continue</Label>
                    <Input
                      id="password-2fa"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      data-testid="password-input"
                    />
                  </div>
                  <Button onClick={handleEnable2FA} disabled={!password || loading} data-testid="enable-2fa-btn">
                    <Smartphone className="h-4 w-4 mr-2" />
                    Enable 2FA
                  </Button>
                </div>
              )}

              {qrCode && !twoFactorEnabled && (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Step 1: Scan QR Code</h3>
                    <div className="border rounded-md p-4 inline-block bg-white">
                      <img src={qrCode} alt="2FA QR Code" className="w-64 h-64" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Secret: <code className="bg-muted px-2 py-1 rounded text-xs">{secret}</code>
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Step 2: Enter Verification Code</h3>
                    <Label htmlFor="verification-code">6-digit code from your app</Label>
                    <Input
                      id="verification-code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      data-testid="verification-code-input"
                    />
                  </div>

                  <Button onClick={handleVerify2FA} disabled={verificationCode.length !== 6 || loading} data-testid="verify-2fa-btn">
                    Verify and Enable
                  </Button>
                </div>
              )}

              {recoveryCodes.length > 0 && (
                <div className="space-y-2 border rounded-md p-4 bg-yellow-50">
                  <h3 className="font-semibold text-yellow-900">⚠️ Save Your Recovery Codes</h3>
                  <p className="text-sm text-yellow-800">
                    Store these codes in a safe place. You can use them to access your account if you lose your phone.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {recoveryCodes.map((code, i) => (
                      <code key={i} className="bg-white px-3 py-2 rounded text-sm border">{code}</code>
                    ))}
                  </div>
                  <Button variant="outline" onClick={() => {
                    const text = recoveryCodes.join('\n');
                    navigator.clipboard.writeText(text);
                    toast.success('Recovery codes copied');
                  }}>
                    Copy All Codes
                  </Button>
                </div>
              )}

              {twoFactorEnabled && (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <Shield className="h-4 w-4 text-green-700" />
                    <AlertDescription className="text-green-900">
                      2FA is currently enabled on your account
                    </AlertDescription>
                  </Alert>
                  <div>
                    <Label htmlFor="password-disable">Enter password to disable 2FA</Label>
                    <Input
                      id="password-disable"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                    />
                  </div>
                  <Button variant="destructive" onClick={handleDisable2FA} disabled={!password || loading}>
                    Disable 2FA
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SESSIONS TAB */}
        <TabsContent value="sessions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Sessions</CardTitle>
              <CardDescription>Manage logged-in devices and browsers</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{session.user_name}</div>
                          <div className="text-xs text-muted-foreground">{session.user_email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{session.ip_address}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{session.user_agent}</TableCell>
                      <TableCell className="text-sm">{new Date(session.last_activity).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeSession(session.id)}>
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No active sessions
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGIN HISTORY TAB */}
        <TabsContent value="login-history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Login History</CardTitle>
              <CardDescription>Review all login attempts</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.map((login) => (
                    <TableRow key={login.id}>
                      <TableCell className="text-sm">{new Date(login.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{login.user_name}</div>
                          <div className="text-xs text-muted-foreground">{login.user_email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{login.ip_address}</TableCell>
                      <TableCell>
                        {login.success ? (
                          <Badge className="bg-green-50 text-green-700 border-green-200">Success</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {loginHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No login history
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* IP BLOCKING TAB */}
        <TabsContent value="ip-blocking" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={blockIPDialog} onOpenChange={setBlockIPDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Ban className="h-4 w-4 mr-2" />
                  Block IP
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Block IP Address</DialogTitle>
                  <DialogDescription>Prevent access from a specific IP</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>IP Address</Label>
                    <Input
                      value={blockIPForm.ip_address}
                      onChange={(e) => setBlockIPForm({ ...blockIPForm, ip_address: e.target.value })}
                      placeholder="192.168.1.1"
                    />
                  </div>
                  <div>
                    <Label>Reason</Label>
                    <Input
                      value={blockIPForm.reason}
                      onChange={(e) => setBlockIPForm({ ...blockIPForm, reason: e.target.value })}
                      placeholder="Suspicious activity"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBlockIPDialog(false)}>Cancel</Button>
                  <Button onClick={handleBlockIP} disabled={loading}>Block IP</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Blocked At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedIPs.map((block) => (
                  <TableRow key={block.id}>
                    <TableCell className="font-mono">{block.ip_address}</TableCell>
                    <TableCell>{block.reason}</TableCell>
                    <TableCell className="text-sm">{new Date(block.blocked_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleUnblockIP(block.id)}>
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {blockedIPs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No blocked IPs
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ANOMALIES TAB */}
        <TabsContent value="anomalies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detected Anomalies</CardTitle>
              <CardDescription>Suspicious activity and security alerts</CardDescription>
            </CardHeader>
            <CardContent>
              {anomalies.length > 0 ? (
                <div className="space-y-3">
                  {anomalies.map((anomaly, i) => (
                    <Alert key={i} className="border-red-200 bg-red-50">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-900">
                        <div className="font-semibold">{anomaly.type.replace(/_/g, ' ').toUpperCase()}</div>
                        <div className="text-sm">{anomaly.description}</div>
                        <div className="text-xs text-red-700 mt-1">
                          Severity: <Badge variant="outline" className="bg-red-100 border-red-300">{anomaly.severity}</Badge>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No anomalies detected
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
